// src/api/overviewApi.js
// API integration with Django backend API endpoints

import apiClient from "./config";

// ---------- Fetch Clients List ----------
export const fetchClients = async () => {
  try {
    const response = await apiClient.get("/api/clients/");
    console.log("Clients API Response:", response.data);
    const data = response.data;

    if (data && data.clients) {
      return data.clients.map((client) => ({
        ...client,
        hasHelpdesk: client.hasHelpdesk || Boolean(client.has_freshservice || client.freshservice_workspace_id),
      }));
    }

    if (data && Array.isArray(data)) {
      return data;
    }

    console.warn("Unexpected clients response format:", data);
    return [];
  } catch (error) {
    console.error("Error fetching clients:", error);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    }
    return [];
  }
};

// ---------- Summary Stats (Connected to Backend) ----------
/**
 * Map raw combined API response data to the frontend stats format.
 * Accepts either raw API data (already fetched) or fetches from API if no data provided.
 * This avoids a duplicate API call when used alongside fetchOverviewCombined.
 */
export const mapOverviewStats = (data) => {
  const overviewCards = data.overview_cards || {};
  return {
    open_tickets_count_90days: overviewCards.open_tickets?.count_90days || 0,
    open_tickets_count_total: overviewCards.open_tickets?.count_total || 0,
    open_tickets_count: overviewCards.open_tickets?.count_90days || 0,
    high_priority_open_count: overviewCards.open_tickets?.high_priority_count || 0,
    new_tickets_7days: overviewCards.new_tickets_7days?.count ?? 0,
    old_tickets_30plus_days: overviewCards.old_tickets_30plus_days?.count ?? 0,
    secured_devices_percentage: overviewCards.secured_devices?.percentage || 0,
    total_devices_count: overviewCards.secured_devices?.total_devices_count ?? 0,
    has_soc_contract: overviewCards.secured_devices?.has_soc_contract ?? false,
    month_open_tickets: overviewCards.secured_devices?.month_open_tickets || 0,
    year_open_tickets: overviewCards.secured_devices?.year_open_tickets || 0,
    total_assets_count: overviewCards.total_assets?.count || 0,
    emergency_tickets_count: overviewCards.emergency_tickets?.count || 0,
  };
};

export const fetchOverviewStats = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/overview/combined/", {
      params: params,
    });

    console.log("Overview Stats API Response:", response.data);
    const stats = mapOverviewStats(response.data);
    console.log("Mapped stats:", stats);
    return stats;
  } catch (error) {
    console.error("Error fetching overview stats:", error);
    return {
      open_tickets_count_90days: 0,
      open_tickets_count_total: 0,
      open_tickets_count: 0,
      high_priority_open_count: 0,
      new_tickets_7days: 0,
      old_tickets_30plus_days: 0,
      secured_devices_percentage: 0,
      total_devices_count: 0,
      has_soc_contract: false,
      total_assets_count: 0,
      emergency_tickets_count: 0,
      month_open_tickets: 0,
      year_open_tickets: 0,
    };
  }
};

// ---------- New API: /api/open-tickets/queues/ ----------
export const fetchTicketQueues = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/open-tickets/queues/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching ticket queues:", error);
    return { queues: [] };
  }
};

// ---------- New API: /api/open-tickets/by-age/ ----------
export const fetchTicketsByAge = async (clientId = null, startDate = null, endDate = null, signal = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const config = { params };
    if (signal) config.signal = signal;

    const response = await apiClient.get("/api/open-tickets/by-age/", config);

    // Check if response has valid data
    if (response.data && response.data.labels && response.data.labels.length > 0) {
      return response.data;
    }

    // Return dummy data if API returns empty or invalid data
    return {
      labels: [
        "0-1 days",
        "2-3 days",
        "4-5 days",
        "6-7 days",
        "8-10 days",
        "11-14 days",
        "15-20 days",
        "21-30 days",
        "31-45 days",
        "46-60 days",
        "61-75 days",
        "76-90 days",
        "90-120 days",
        "120+ days",
      ],
      values: [190, 165, 140, 120, 100, 85, 70, 55, 45, 35, 25, 18, 12, 5],
    };
  } catch (error) {
    // Re-throw abort errors so callers can distinguish cancellation from failure
    if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      throw error;
    }
    if (process.env.NODE_ENV === 'development' && !error.code) {
      console.debug("Tickets by age unavailable:", error.message);
    }
    return {
      labels: [
        "0-1 days",
        "2-3 days",
        "4-5 days",
        "6-7 days",
        "8-10 days",
        "11-14 days",
        "15-20 days",
        "21-30 days",
        "31-45 days",
        "46-60 days",
        "61-75 days",
        "76-90 days",
        "90-120 days",
        "120+ days",
      ],
      values: [190, 165, 140, 120, 100, 85, 70, 55, 45, 35, 25, 18, 12, 5],
    };
  }
};

export const fetchTicketsByAgeForQueue = async (clientId, queueId, startDate = null, endDate = null) => {
  try {
    const params = { queue_id: queueId };
    if (clientId) {
      params.client_id = clientId;
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const response = await apiClient.get("/api/open-tickets/by-age/", { params });
    return response.data;
  } catch (error) {
    if (process.env.NODE_ENV === 'development' && !error.code) {
      console.debug("Tickets by age for queue unavailable:", error.message);
    }
    return { labels: [], values: [] };
  }
};

export const fetchTicketsByStatusForQueue = async (clientId, queueId, selectedDate = null) => {
  try {
    const params = { queue_id: queueId };
    if (clientId) {
      params.client_id = clientId;
    }
    if (selectedDate) {
      params.selected_date = selectedDate;
    }

    const response = await apiClient.get("/api/open-tickets/by-status/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching tickets by status for queue:", error);
    return { labels: [], values: [] };
  }
};

/**
 * Fetch ticket data by queue with date range (quarterly view)
 * Returns: queues (x-axis), ticket_counts, avg_ages (y-axis), date_range
 * Defaults to last 90 days if no dates provided
 */
export const fetchTicketsByQueueQuarterly = async (clientId, startDate = null, endDate = null, status = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (startDate) {
      // Format date as YYYY-MM-DD
      const d = new Date(startDate);
      params.start_date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    if (endDate) {
      const d = new Date(endDate);
      params.end_date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    if (status) {
      params.status = status;
    }

    const response = await apiClient.get("/api/open-tickets/by-queue-quarterly/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching tickets by queue quarterly:", error);
    return { 
      queues: [], 
      ticket_counts: [], 
      avg_ages: [], 
      date_range: { start_date: null, end_date: null },
      total_tickets: 0 
    };
  }
};

/**
 * Fetch available statuses for queue quarterly view
 * Returns: list of status names available for the client within date range
 */
export const fetchQueueQuarterlyAvailableStatuses = async (clientId, startDate = null, endDate = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (startDate) {
      const d = new Date(startDate);
      params.start_date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    if (endDate) {
      const d = new Date(endDate);
      params.end_date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    const response = await apiClient.get("/api/open-tickets/queue-quarterly-available-statuses/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching available statuses:", error);
    return { statuses: [] };
  }
};

export const fetchAvailableTicketDates = async (clientId, queueId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (queueId) {
      params.queue_id = queueId;
    }

    const response = await apiClient.get("/api/open-tickets/available-dates/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching available ticket dates:", error);
    return { dates: [], count: 0 };
  }
};

export const fetchTicketTrendsMonthly = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/ticket-trends/monthly/", { params });
    
    if (response.data && response.data.labels && response.data.labels.length > 0) {
      return response.data;
    }
    
    return {
      labels: [],
      values: [],
    };
  } catch (error) {
    console.error("Error fetching ticket trends:", error);
    return {
      labels: [],
      values: [],
    };
  }
};

export const fetchSecurityThreatSources = async (clientId = null, selectedDate = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (selectedDate) {
      const date = new Date(selectedDate);
      params.selected_date = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    const response = await apiClient.get("/api/security/threat-sources/", { params });
    
    if (response.data && response.data.sources && response.data.sources.length > 0) {
      return response.data.sources;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching threat sources:", error);
    return [];
  }
};

export const fetchSocAvailableDates = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/security/soc-available-dates/", { params });
    
    if (response.data && response.data.available_dates) {
      return response.data.available_dates;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching SOC available dates:", error);
    return [];
  }
};

export const fetchEndpointDetails = async (clientId = null, selectedDate = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (selectedDate) {
      const date = new Date(selectedDate);
      params.selected_date = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    const response = await apiClient.get("/api/security/endpoint-details/", { params });
    
    if (response.data) {
      return {
        labels: response.data.labels || [],
        values: response.data.values || [],
        colors: response.data.colors || []
      };
    }
    
    return { labels: [], values: [], colors: [] };
  } catch (error) {
    console.error("Error fetching endpoint details:", error);
    return { labels: [], values: [], colors: [] };
  }
};

export const fetchEndpointAvailableDates = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/security/endpoint-available-dates/", { params });
    
    if (response.data && response.data.available_dates) {
      return response.data.available_dates;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching endpoint available dates:", error);
    return [];
  }
};

export const fetchSecurityRecentAlerts = async (clientId = null, limit = 20) => {
  try {
    const params = { limit };
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/security/recent-alerts/", { params });
    
    if (response.data && response.data.alerts && response.data.alerts.length > 0) {
      return response.data.alerts;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching security alerts:", error);
    return [];
  }
};

// ---------- Network Threat Sources ----------
export const fetchNetworkThreatSources = async (clientId = null, date = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (date) {
      params.date = date;
      console.log("fetchNetworkThreatSources - sending date:", date);
    }

    const response = await apiClient.get("/api/network/threat-sources/", { params });
    
    if (response.data && response.data.sources && response.data.sources.length > 0) {
      return response.data.sources;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching network threat sources:", error);
    return [];
  }
};

export const fetchNocAvailableDates = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/network/noc-available-dates/", { params });
    
    if (response.data && response.data.available_dates) {
      return response.data.available_dates;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching NOC available dates:", error);
    return [];
  }
};

// ---------- Combined Overview Data (single API call) ----------
export const fetchOverviewCombined = async (clientId = null, signal = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }

    const config = { params };
    if (signal) config.signal = signal;

    const response = await apiClient.get("/api/overview/combined/", config);
    return response.data;
  } catch (error) {
    if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      // Request was cancelled due to client switch — not an error
      return null;
    }
    console.error("Error fetching combined overview:", error);
    return null;
  }
};

// ---------- Network Recent Alerts ----------
export const fetchNetworkRecentAlerts = async (clientId = null, limit = 20) => {
  try {
    const params = { limit };
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/network/recent-alerts/", { params });
    
    if (response.data && response.data.alerts && response.data.alerts.length > 0) {
      return response.data.alerts;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching network alerts:", error);
    return [];
  }
};

// ---------- Ticket Priority Distribution ----------
export const fetchTicketPriorityDistribution = async (clientId = null, selectedDate = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (selectedDate) {
      params.date = selectedDate;
    }

    const response = await apiClient.get("/api/overview/ticket-priority-distribution/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching ticket priority distribution:", error);
    return { labels: [], values: [], colors: [] };
  }
};

// ---------- Tickets by Status with Priority ----------
export const fetchTicketsByStatusWithPriority = async (clientId = null, startDate = null, endDate = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const response = await apiClient.get("/api/overview/tickets-by-status-priority/", { params });
    return response.data;
  } catch (error) {
    return { labels: [], high_priority: [], normal_priority: [] };
  }
};

export const fetchRecentOpenTickets = async (clientId = null, limit = 100, startDate = null, endDate = null) => {
  try {
    const params = { limit };
    if (clientId) {
      params.client_id = clientId;
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const response = await apiClient.get("/api/overview/recent-open-tickets/", { params });

    if (response.data && response.data.tickets) {
      return {
        tickets: response.data.tickets || [],
        total: response.data.total || 0,
      };
    }

    return { tickets: [], total: 0 };
  } catch (error) {
    console.error("Error fetching recent open tickets:", error);
    return { tickets: [], total: 0 };
  }
};

// ---------- Ticket Detail ----------
export const fetchTicketDetail = async (ticketNumber) => {
  try {
    const response = await apiClient.get(`/api/overview/ticket-detail/${ticketNumber}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ticket detail for ${ticketNumber}:`, error);
    return null;
  }
};

// ---------- Client Team Details ----------
export const fetchClientTeamDetails = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }

    const response = await apiClient.get("/api/overview/client-team-details/", { params });

    if (response.data) {
      return {
        team_details: response.data.team_details || [],
        total: response.data.total || 0,
        client_name: response.data.client_name || "",
      };
    }

    return { team_details: [], total: 0, client_name: "" };
  } catch (error) {
    console.error("Error fetching client team details:", error);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }
    return { team_details: [], total: 0, client_name: "" };
  }
};

// ---------- Resources ----------
// TODO: Resources endpoint not yet implemented on backend.
// This returns an empty array until a dedicated /api/resources/ endpoint is created.
export const fetchResources = async (clientId = null) => {
  return [];
};

// ---------- Tickets Created vs Closed ----------
export const fetchTicketsCreatedClosed = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    const response = await apiClient.get("/api/overview/tickets-created-closed/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching tickets created/closed:", error);
    return {
      last_24h:  { created: 0, closed: 0, resolved_pct: null },
      last_7d:   { created: 0, closed: 0, resolved_pct: null },
      last_30d:  { created: 0, closed: 0, resolved_pct: null },
    };
  }
};

// ---------- Notifications ----------
export const fetchNotifications = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    const response = await apiClient.get("/api/notifications/list/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return { tickets: [], total: 0 };
  }
};

export const markNotificationsAsRead = async (clientId = null, ticketIds = []) => {
  try {
    const body = {};
    if (clientId) body.client_id = clientId;
    if (ticketIds.length) body.ticket_ids = ticketIds;
    const response = await apiClient.post("/api/notifications/mark-read/", body);
    return response.data;
  } catch (error) {
    console.error("Error marking notifications as read:", error);
    throw error;
  }
};

export const markTicketAsRead = async (ticketId, clientId = null) => {
  try {
    await apiClient.post("/api/notifications/mark-ticket-read/", {
      ticket_id: ticketId,
      client_id: clientId,
    });
  } catch (error) {
    console.error("Error marking ticket as read:", error);
  }
};

export const markTicketsAsUnread = async (ticketIds, clientId = null) => {
  try {
    await apiClient.post("/api/notifications/mark-tickets-unread/", {
      ticket_ids: ticketIds,
      client_id: clientId,
    });
  } catch (error) {
    console.error("Error marking tickets as unread:", error);
  }
};

export const fetchNotificationCount = async (clientId = null) => {
  try {
    const params = {};
    if (clientId) params.client_id = clientId;

    const response = await apiClient.get('/api/notifications/count/', {
      params,
    });

    return response.data.count || 0;
  } catch (error) {
    if (process.env.NODE_ENV === 'development' && !error.code) {
      console.debug('Notification count unavailable:', error.response?.data || error.message);
    }
    return 0;
  }
};


// ---------- Security Overview ----------
export const fetchSecurityOverview = async (clientId = null) => {
  // Return dummy data when no client is selected
  if (!clientId) {
    return {
      summary: {
        totalSocEndpoints: 1418,
        uniqueEndpointTypes: 8,
        topEndpoint: "Microsoft Defender",
        topEndpointCount: 494,
        lastUpdated: "Jan 2024",
      },
      endpointDistribution: {
        labels: ["KnowBe4 Licenced Seats", "KnowBe4 Used Seats", "Rapid7 IDR", "Rapid7 iVM", "CrowdStrike", "DarkWeb ID Domains Being Searched"],
        values: [250, 180, 320, 280, 150, 238],
        colors: ["#667eea", "#764ba2", "#43e97b", "#fa709a", "#4facfe", "#30cfd0"],
      },
      endpointDetails: {
        labels: ["KnowBe4 Licenced Seats", "KnowBe4 Used Seats", "Rapid7 IDR", "Rapid7 iVM", "CrowdStrike", "DarkWeb ID Domains Being Searched"],
        values: [250, 180, 320, 280, 150, 238],
        colors: ["#667eea", "#764ba2", "#43e97b", "#fa709a", "#4facfe", "#30cfd0"],
      },
      monthlyEndpointTrend: {
        labels: ["Jan 2025", "Feb 2025", "Mar 2025", "Apr 2025", "May 2025", "Jun 2025", "Jul 2025", "Aug 2025", "Sep 2025"],
        endpoints: {
          "DarkWeb ID Domains Being Searched": [0, 0, 0, 0, 0, 0, 0, 0, 0],
          "KnowBe4 Used Seats": [120, 150, 170, 180, 140, 190, 140, 170, 180],
          CrowdStrike: [140, 170, 190, 200, 160, 210, 160, 190, 200],
          "KnowBe4 Licenced Seats": [170, 210, 240, 260, 200, 270, 210, 230, 250],
          "Rapid7 IDR": [340, 400, 450, 510, 390, 540, 410, 450, 490],
          "Rapid7 iVM": [350, 410, 460, 520, 400, 550, 420, 470, 500],
        },
        colors: {
          "DarkWeb ID Domains Being Searched": "#9b59b6",
          "KnowBe4 Used Seats": "#3498db",
          CrowdStrike: "#1abc9c",
          "KnowBe4 Licenced Seats": "#e74c3c",
          "Rapid7 IDR": "#2ecc71",
          "Rapid7 iVM": "#f39c12",
        },
      },
      alertsTable: [
        {
          id: "SEC-2025-001",
          type: "Malware",
          device: "Workstation-042",
          severity: "Critical",
          timestamp: "2 hours ago",
        },
        {
          id: "SEC-2025-002",
          type: "Intrusion",
          device: "Server-DB-01",
          severity: "High",
          timestamp: "5 hours ago",
        },
        {
          id: "SEC-2025-003",
          type: "Malware",
          device: "Workstation-128",
          severity: "Medium",
          timestamp: "1 day ago",
        },
        {
          id: "SEC-2025-004",
          type: "Intrusion",
          device: "Server-WEB-03",
          severity: "High",
          timestamp: "2 days ago",
        },
        {
          id: "SEC-2025-005",
          type: "Unknown",
          device: "Workstation-056",
          severity: "Low",
          timestamp: "3 days ago",
        },
        {
          id: "SEC-2025-006",
          type: "Malware",
          device: "Workstation-091",
          severity: "Critical",
          timestamp: "4 days ago",
        },
        {
          id: "SEC-2025-007",
          type: "Intrusion",
          device: "Server-APP-02",
          severity: "Medium",
          timestamp: "5 days ago",
        },
        {
          id: "SEC-2025-008",
          type: "Malware",
          device: "Workstation-203",
          severity: "Low",
          timestamp: "1 week ago",
        },
      ],
    };
  }

  try {
    const response = await apiClient.get("/api/security/overview/", {
      params: { client_id: clientId },
    });
    const data = response.data;

    // Parse endpoint distribution data
    let endpointDistribution = {
      labels: [],
      values: [],
      colors: [],
    };

    // If endpoint data is available, use it
    if (data.security_endpoint_data) {
      try {
        const endpointData = typeof data.security_endpoint_data === "string" ? JSON.parse(data.security_endpoint_data) : data.security_endpoint_data;

        if (Array.isArray(endpointData) && endpointData.length > 0) {
          // Aggregate by endpoint name
          const totals = {};
          endpointData.forEach((row) => {
            const endpointName = row.endpoint || row.name || "Uncategorized";
            const count = parseInt(row.endpoint_count || row.count || 0);
            totals[endpointName] = (totals[endpointName] || 0) + count;
          });

          const labels = Object.keys(totals);
          const values = labels.map((label) => totals[label]);

          // Use colors from backend if available, otherwise use default colors
          const defaultColors = ["#667eea", "#764ba2", "#43e97b", "#fa709a", "#4facfe", "#30cfd0", "#a8edea", "#f093fb"];
          const colors = data.security_type_status_data?.colors
            ? typeof data.security_type_status_data.colors === "string"
              ? JSON.parse(data.security_type_status_data.colors)
              : data.security_type_status_data.colors
            : defaultColors.slice(0, labels.length);

          endpointDistribution = {
            labels,
            values,
            colors: colors.length >= labels.length ? colors.slice(0, labels.length) : [...colors, ...defaultColors.slice(colors.length, labels.length)],
          };
        }
      } catch (error) {
        console.error("Error parsing endpoint data:", error);
      }
    }

    // Process monthly endpoint trend data
    const monthlyEndpointTrend = (() => {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      // Default empty data
      let trendData = {
        labels: [],
        endpoints: {},
        colors: {},
      };

      if (data.security_endpoint_trend_data) {
        try {
          const trendDataRaw = typeof data.security_endpoint_trend_data === "string" ? JSON.parse(data.security_endpoint_trend_data) : data.security_endpoint_trend_data;

          if (Array.isArray(trendDataRaw) && trendDataRaw.length > 0) {
            const endpointData = {};
            const monthSet = new Set();

            trendDataRaw.forEach((row) => {
              const endpointName = row.endpoint || row.name || "Uncategorized";
              const year = row.year_nb || row.year;
              const month = row.month_nb || row.month;
              const count = parseInt(row.endpoint_count || row.count || 0);

              if (year && month) {
                const monthKey = `${year}-${String(month).padStart(2, "0")}`;
                monthSet.add(monthKey);

                if (!endpointData[endpointName]) {
                  endpointData[endpointName] = {};
                }
                endpointData[endpointName][monthKey] = (endpointData[endpointName][monthKey] || 0) + count;
              }
            });

const today = new Date();
// Stop at n-1 month: data for current month isn't available yet
const prevMonth = today.getMonth() === 0 ? 12 : today.getMonth(); // getMonth() is 0-indexed, so getMonth()==0 means Jan, prev=Dec
const prevYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
const allMonthKeys = [];

// Find the earliest month in data
const sortedExistingKeys = Array.from(monthSet).sort();
const earliestKey = sortedExistingKeys.at(0); // e.g. "2025-01"

if (earliestKey) {
    const [startYear, startMonth] = earliestKey.split("-").map(Number);
    let year = startYear;
    let month = startMonth;

    // Walk from earliest month to previous month (n-1), current month has no data yet
    while (year < prevYear || (year === prevYear && month <= prevMonth)) {
        allMonthKeys.push(`${year}-${String(month).padStart(2, "0")}`);
        month++;
        if (month > 12) {
            month = 1;
            year++;
        }
    }
}

const labels = allMonthKeys.map((key) => {
    const [year, month] = key.split("-");
    return `${monthNames[parseInt(month) - 1]} ${year}`;
});

const endpoints = {};
Object.keys(endpointData).forEach((endpointName) => {
    endpoints[endpointName] = allMonthKeys.map((key) => endpointData[endpointName][key] || 0);  // 0 for missing months
});

            const distinctColors = ["#9b59b6", "#3498db", "#1abc9c", "#e74c3c", "#2ecc71", "#f39c12", "#e67e22", "#34495e"];
            const colors = {};
            Object.keys(endpoints).forEach((endpointName, idx) => {
              colors[endpointName] = distinctColors[idx % distinctColors.length];
            });

            trendData = {
              labels,
              endpoints,
              colors,
            };
          }
        } catch (error) {
          console.error("Error parsing endpoint trend data:", error);
        }
      }

      return trendData;
    })();

    // Check if there's actual data from backend
    const totalEndpoints = data.security_total_endpoints ?? 0;
    const hasEndpointData = data.security_endpoint_data && 
                           Array.isArray(data.security_endpoint_data) && 
                           data.security_endpoint_data.length > 0;
    const hasAlerts = data.security_recent_alerts && 
                     Array.isArray(data.security_recent_alerts) && 
                     data.security_recent_alerts.length > 0;
    
    // Check if endpointDistribution has real data (not empty)
    const hasDistributionData = endpointDistribution.values && 
                                endpointDistribution.values.length > 0 && 
                                endpointDistribution.values.some(v => v > 0);
    
    // Check if monthlyEndpointTrend has real data
    const hasTrendData = monthlyEndpointTrend.endpoints && 
                        Object.keys(monthlyEndpointTrend.endpoints).length > 0 &&
                        Object.values(monthlyEndpointTrend.endpoints).some(arr => Array.isArray(arr) && arr.some(v => v > 0));

    // If no data exists, return null
    if (totalEndpoints === 0 && 
        !hasEndpointData && 
        !hasDistributionData && 
        !hasTrendData && 
        !hasAlerts) {
      return null;
    }

    return {
      summary: {
        totalSocEndpoints: totalEndpoints,
        uniqueEndpointTypes: data.security_endpoint_types_count ?? 0,
        topEndpoint: data.security_top_endpoint_name ?? "",
        topEndpointCount: data.security_top_endpoint_count ?? 0,
        lastUpdated: data.security_last_updated_str ?? "",
      },
      endpointDistribution,
      endpointDetails: endpointDistribution,
      monthlyEndpointTrend,
      alertsTable: data.security_recent_alerts || [],
    };
  } catch (error) {
    console.error("Error fetching security overview:", error);
    return null;
  }
};

// ---------- Network Overview ----------
export const fetchNetworkOverview = async (clientId = null, date = null) => {
  // Return null when no client is selected
  if (!clientId) {
    return null;
  }

  try {
    const params = { client_id: clientId };
    if (date) {
      params.date = date;
      console.log("fetchNetworkOverview - sending date:", date);
    }
    const response = await apiClient.get("/api/network/overview/", {
      params,
    });
    const data = response.data;

    // Process endpoint distribution data for NOC
    let endpointDistribution = {
      labels: [],
      values: [],
      colors: [],
    };

    // If endpoint data is available, use it
    if (data.network_endpoint_data) {
      try {
        const endpointData = typeof data.network_endpoint_data === "string" ? JSON.parse(data.network_endpoint_data) : data.network_endpoint_data;

        if (Array.isArray(endpointData) && endpointData.length > 0) {
          // Aggregate by endpoint name
          const totals = {};
          endpointData.forEach((row) => {
            const endpointName = row.endpoint || row.name || "Uncategorized";
            const count = parseInt(row.endpoint_count || row.count || 0);
            totals[endpointName] = (totals[endpointName] || 0) + count;
          });

          const labels = Object.keys(totals);
          const values = labels.map((label) => totals[label]);

          // Use colors from backend if available, otherwise use default colors
          const defaultColors = ["#667eea", "#764ba2", "#43e97b", "#fa709a", "#4facfe", "#30cfd0", "#a8edea", "#f093fb"];
          const colors = data.network_status_data?.colors
            ? typeof data.network_status_data.colors === "string"
              ? JSON.parse(data.network_status_data.colors)
              : data.network_status_data.colors
            : defaultColors.slice(0, labels.length);

          endpointDistribution = {
            labels,
            values,
            colors: colors.length >= labels.length ? colors.slice(0, labels.length) : [...colors, ...defaultColors.slice(colors.length, labels.length)],
          };
        }
      } catch (error) {
        console.error("Error parsing network endpoint data:", error);
      }
    }

    // Process monthly endpoint trend data
    const monthlyEndpointTrend = (() => {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      // Default empty data
      let trendData = {
        labels: [],
        endpoints: {},
        colors: {},
      };

      if (data.network_endpoint_trend_data) {
        try {
          const trendDataRaw = typeof data.network_endpoint_trend_data === "string" ? JSON.parse(data.network_endpoint_trend_data) : data.network_endpoint_trend_data;

          if (Array.isArray(trendDataRaw) && trendDataRaw.length > 0) {
            const endpointData = {};
            const monthSet = new Set();

            trendDataRaw.forEach((row) => {
              const endpointName = row.endpoint || row.name || "Uncategorized";
              const year = row.year_nb || row.year;
              const month = row.month_nb || row.month;
              const count = parseInt(row.endpoint_count || row.count || 0);

              if (year && month) {
                const monthKey = `${year}-${String(month).padStart(2, "0")}`;
                monthSet.add(monthKey);

                if (!endpointData[endpointName]) {
                  endpointData[endpointName] = {};
                }
                endpointData[endpointName][monthKey] = (endpointData[endpointName][monthKey] || 0) + count;
              }
            });

            const today = new Date();
            // Stop at n-1 month: data for current month isn't available yet
            const prevMonth = today.getMonth() === 0 ? 12 : today.getMonth();
            const prevYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
            const allMonthKeys = [];
            const sortedExistingKeys = Array.from(monthSet).sort();
            const earliestKey = sortedExistingKeys.at(0);

            if (earliestKey) {
              const [startYear, startMonth] = earliestKey.split("-").map(Number);
              let year = startYear;
              let month = startMonth;

              // Walk from earliest month to previous month (n-1), current month has no data yet
              while (year < prevYear || (year === prevYear && month <= prevMonth)) {
                allMonthKeys.push(`${year}-${String(month).padStart(2, "0")}`);
                month++;
                if (month > 12) { month = 1; year++; }
              }
            }

            const labels = allMonthKeys.map((key) => {
              const [year, month] = key.split("-");
              return `${monthNames[parseInt(month) - 1]} ${year}`;
            });

            const endpoints = {};
            Object.keys(endpointData).forEach((endpointName) => {
              endpoints[endpointName] = allMonthKeys.map((key) => endpointData[endpointName][key] || 0);
            });

            const distinctColors = ["#9b59b6", "#3498db", "#1abc9c", "#e74c3c", "#2ecc71", "#f39c12", "#e67e22", "#34495e"];
            const colors = {};
            Object.keys(endpoints).forEach((endpointName, idx) => {
              colors[endpointName] = distinctColors[idx % distinctColors.length];
            });

            trendData = {
              labels,
              endpoints,
              colors,
            };
          }
        } catch (error) {
          console.error("Error parsing network endpoint trend data:", error);
        }
      }

      return trendData;
    })();

    // Check if there's actual data from backend
    const totalEndpoints = data.network_total_endpoints ?? 0;
    let parsedEndpointData = null;
    try {
      parsedEndpointData = data.network_endpoint_data ? 
        (typeof data.network_endpoint_data === 'string' ? JSON.parse(data.network_endpoint_data) : data.network_endpoint_data) : null;
    } catch (e) {
      parsedEndpointData = null;
    }
    const hasEndpointData = parsedEndpointData && 
                           Array.isArray(parsedEndpointData) && 
                           parsedEndpointData.length > 0;
    const hasAlerts = data.network_recent_alerts && 
                     Array.isArray(data.network_recent_alerts) && 
                     data.network_recent_alerts.length > 0;
    
    // Check if endpointDistribution has real data (not empty)
    const hasDistributionData = endpointDistribution.values && 
                                endpointDistribution.values.length > 0 && 
                                endpointDistribution.values.some(v => v > 0);
    
    // Check if monthlyEndpointTrend has real data
    const hasTrendData = monthlyEndpointTrend.endpoints && 
                        Object.keys(monthlyEndpointTrend.endpoints).length > 0 &&
                        Object.values(monthlyEndpointTrend.endpoints).some(arr => Array.isArray(arr) && arr.some(v => v > 0));

    // Process endpoint details data (from network_endpoint_details_data)
    let endpointDetails = {
      labels: [],
      values: [],
      colors: [],
    };

    if (data.network_endpoint_details_data) {
      try {
        const detailsDataRaw = typeof data.network_endpoint_details_data === "string" ? JSON.parse(data.network_endpoint_details_data) : data.network_endpoint_details_data;

        if (Array.isArray(detailsDataRaw) && detailsDataRaw.length > 0) {
          const endpoints = {};
          detailsDataRaw.forEach((row) => {
            const endpointName = row.endpoint || row.name || "Uncategorized";
            const count = parseInt(row.endpoint_count || row.count || 0);
            endpoints[endpointName] = (endpoints[endpointName] || 0) + count;
          });

          const labels = Object.keys(endpoints);
          const values = labels.map((label) => endpoints[label]);

          // Filter out endpoints with no data (count <= 0)
          const filteredLabels = [];
          const filteredValues = [];
          labels.forEach((label, i) => {
            if (values[i] > 0) {
              filteredLabels.push(label);
              filteredValues.push(values[i]);
            }
          });

          // Use colors from backend if available, otherwise use default colors
          const defaultColors = ["#667eea", "#764ba2", "#43e97b", "#fa709a", "#4facfe", "#30cfd0", "#a8edea", "#f093fb"];
          const colors = defaultColors.slice(0, filteredLabels.length);

          endpointDetails = {
            labels: filteredLabels,
            values: filteredValues,
            colors: colors.length >= filteredLabels.length ? colors.slice(0, filteredLabels.length) : [...colors, ...defaultColors.slice(colors.length, filteredLabels.length)],
          };
          
          console.log("Processed endpointDetails from network_endpoint_details_data:", endpointDetails);
        }
      } catch (error) {
        console.error("Error parsing network endpoint details data:", error);
        endpointDetails = endpointDistribution; // Fallback to distribution
      }
    } else {
      // Fallback to distribution if details not available
      endpointDetails = endpointDistribution;
    }

    // Always return data structure, even if values are 0
    // This allows the UI to display cards with 0 values instead of showing "no data" message
    return {
      summary: {
        totalNocEndpoints: totalEndpoints,
        uniqueEndpointTypes: data.network_endpoint_types_count ?? 0,
        topEndpoint: data.network_top_endpoint_name ?? "",
        topEndpointCount: data.network_top_endpoint_count ?? 0,
        lastUpdated: data.network_last_updated_str ?? "",
      },
      endpointDistribution,
      endpointDetails,
      monthlyEndpointTrend,
      alertsTable: data.network_recent_alerts || [],
    };
  } catch (error) {
    console.error("Error fetching network overview:", error);
    return null;
  }
};
/* -------------------------------------------------------
   ENGAGEMENT TAB – DEDICATED API (single call for both engagement + resell)
------------------------------------------------------- */
export const fetchEngagementDealsAndResell = async (clientId = null) => {
  try {
    if (!clientId) {
      return {
        deals: [],
        resell_deals: [],
        total_deals: 0,
        total_resell_deals: 0,
        total_amount: 0,
      };
    }
    const response = await apiClient.get("/api/engagement/deals/", {
      params: { client_id: clientId },
    });
    const data = response.data;
    const deals = data.deals || [];
    const resellDeals = data.resell_deals || [];
    return {
      deals: deals.map((deal) => ({
        deal_id: deal.deal_id,
        deal_name: deal.deal_name,
        client_name: deal.client_name,
        close_date: deal.close_date,
        state: deal.state,
        status: deal.status,
        assigned_to: deal.assigned_to,
        service_areas: deal.service_areas || [],
        has_pdf_path: deal.has_pdf_path,
        categories: deal.categories || [],
      })),
      resell_deals: resellDeals.map((deal) => ({
        deal_id: deal.deal_id,
        deal_name: deal.deal_name,
        client_name: deal.client_name,
        close_date: deal.close_date,
        state: deal.state,
        service_areas: deal.service_areas || [],
        categories: deal.categories || [],
      })),
      total_deals: data.total_deals ?? deals.length,
      total_resell_deals: data.total_resell_deals ?? resellDeals.length,
      total_amount: data.total_amount ?? 0,
    };
  } catch (error) {
    console.error("Error fetching engagement/resell deals:", error);
    return {
      deals: [],
      resell_deals: [],
      total_deals: 0,
      total_resell_deals: 0,
      total_amount: 0,
    };
  }
};

/* -------------------------------------------------------
   LEGACY – use fetchEngagementDealsAndResell for engagement tab
------------------------------------------------------- */
export const fetchEngagementDeals = async (clientId = null) => {
  const { deals } = await fetchEngagementDealsAndResell(clientId);
  return { total_deals: deals.length, deals };
};

export const fetchResellDeals = async (clientId = null) => {
  const { resell_deals } = await fetchEngagementDealsAndResell(clientId);
  return { total_resell_deals: resell_deals.length, deals: resell_deals };
};

/* -------------------------------------------------------
   24-HOUR TICKETS — used by Overview (superadmin + client)
------------------------------------------------------- */

/**
 * Fetch only the count of tickets opened in the last 24 hours.
 * Lightweight — used for the badge on mount and auto-refresh.
 */
export const fetch24hrTicketCount = async (clientId) => {
  try {
    const response = await apiClient.get("/api/tickets-24hrs/", {
      params: { client_id: clientId },
    });
    return response.data.count || 0;
  } catch (error) {
    console.error("Error fetching 24hr ticket count:", error);
    return 0;
  }
};

/**
 * Fetch the full list of tickets opened in the last 24 hours.
 * Used when opening the modal or clicking the refresh button.
 * Pass bustCache=true to add a timestamp param and bypass server cache.
 */
export const fetch24hrTickets = async (clientId, bustCache = false) => {
  try {
    const params = { client_id: clientId };
    if (bustCache) params._ = Date.now();

    const response = await apiClient.get("/api/tickets-24hrs/", { params });
    return {
      tickets: response.data.tickets || [],
      count:   response.data.count   || 0,
    };
  } catch (error) {
    console.error("Error fetching 24hr tickets:", error);
    return { tickets: [], count: 0 };
  }
};

// ---------- Freshservice Overview ----------
export const fetchFreshserviceOverview = async (clientId, dateParams = {}, groupId = null) => {
  try {
    const params = { client_id: clientId, ...dateParams };
    if (groupId) params.group_id = groupId;
    const response = await apiClient.get('/api/freshservice/overview/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching Freshservice overview:', error);
    return null;
  }
};

// ---------- OneView (combined Autotask + Freshservice) ----------
export const fetchOneView = async (clientId) => {
  try {
    const response = await apiClient.get('/api/overview/one-view/', {
      params: { client_id: clientId },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching OneView:', error);
    return null;
  }
};

// ---------- Freshservice Helpdesk Analytics ----------
export const fetchFreshserviceAnalytics = async (clientId, dateParams = {}, groupId = null) => {
  try {
    const params = { client_id: clientId, ...dateParams };
    if (groupId) params.group_id = groupId;
    const response = await apiClient.get('/api/freshservice/analytics/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching Freshservice analytics:', error);
    return null;
  }
};

// Returns { groups, status } — groups is null when the fetch failed or returned a
// malformed payload (an array otherwise, [] included for "client genuinely has no
// groups"); status carries the HTTP status (or null for a network-level failure) so
// callers can decide whether retrying is worth it — a 4xx means the request itself is
// wrong (bad client_id, auth) and will fail identically every time, only a missing
// status or 5xx is a plausibly transient blip.
export const fetchFreshserviceGroups = async (clientId, signal = null) => {
  try {
    const config = { params: { client_id: clientId } };
    if (signal) config.signal = signal;
    const response = await apiClient.get('/api/freshservice/groups/', config);
    const groups = response.data?.groups;
    // A malformed/unexpected payload must not reach callers as a truthy non-array —
    // groups.find(...) downstream would throw, and that failure needs to be reported the
    // same way a network error is, not misread as "this client genuinely has no groups".
    return { groups: Array.isArray(groups) ? groups : null, status: response.status };
  } catch (error) {
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      throw error;
    }
    console.error('Error fetching Freshservice groups:', error);
    return { groups: null, status: error.response?.status ?? null };
  }
};

export const fetchFreshserviceReopenStats = async (clientId, dateParams = {}, groupId = null) => {
  try {
    const params = { client_id: clientId, ...dateParams };
    if (groupId) params.group_id = groupId;
    const response = await apiClient.get('/api/freshservice/reopen-stats/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching Freshservice reopen stats:', error);
    return null;
  }
};

// ---------- Submit Feedback ----------
export const submitFeedback = async (feedbackData) => {
  const response = await apiClient.post("/api/feedback/submit/", feedbackData);
  return response.data;
};

// ---------- Chatbot Name Suggestion ----------
export const submitChatbotNameSuggestion = async (payload) => {
  const response = await apiClient.post("/api/chatbot-name-suggestion/", payload);
  return response.data;
};


// ---------- Client Ticket Insights ----------
export const getClientInsights = async (clientId, startDate, endDate) => {
  const params = { client_id: clientId };
  if (startDate) params.start_date = startDate;
  if (endDate)   params.end_date   = endDate;
  const response = await apiClient.get("/api/overview/client-insights/", { params });
  return response.data;
};

export const getInsightTickets = async (clientId, insightType, filterValue, startDate, endDate) => {
  const params = { client_id: clientId, insight_type: insightType, filter_value: filterValue };
  if (startDate) params.start_date = startDate;
  if (endDate)   params.end_date   = endDate;
  const response = await apiClient.get("/api/overview/client-insights/tickets/", { params });
  return response.data;
};

export const getInsightTicketsCsvUrl = (clientId, insightType, filterValue, startDate, endDate) => {
  const base = apiClient.defaults.baseURL || "";
  const params = new URLSearchParams({
    client_id: clientId,
    insight_type: insightType,
    filter_value: filterValue,
    format: "csv",
    ...(startDate ? { start_date: startDate } : {}),
    ...(endDate   ? { end_date:   endDate   } : {}),
  });
  return `${base}/api/overview/client-insights/tickets/?${params.toString()}`;
};

// ---------- Unresponded Tickets (> N days without activity) ----------
/**
 * Fetch open tickets that have had no activity for more than `thresholdDays` days.
 *
 * @param {number|string|null} clientId      - Client to scope the query to.
 * @param {number}             thresholdDays - Days of inactivity threshold (default 7).
 * @returns {Promise<{ tickets: Array, total: number, threshold_days: number }>}
 *
 * Each ticket object contains:
 *   id, ticket_number, title, status, priority, queue,
 *   days_unresponded, last_activity_date, create_date, autotask_url,
 *   ticket_description, latest_comment { text, title, author, date }
 */
export const fetchUnrespondedTickets = async (clientId = null, thresholdDays = 2) => {
  try {
    const params = { threshold_days: thresholdDays };
    if (clientId) params.client_id = clientId;

    const response = await apiClient.get("/api/tickets/unresponded/", { params });
    return {
      tickets: response.data.tickets || [],
      total: response.data.total || 0,
      threshold_days: response.data.threshold_days || thresholdDays,
    };
  } catch (error) {
    console.error("Error fetching unresponded tickets:", error);
    return { tickets: [], total: 0, threshold_days: thresholdDays };
  }
};

// ---------- Gemini AI Chat Proxy for ticket support ----------
/**
 * Send a chat message to the Gemini AI proxy, scoped to a specific ticket.
 * The API key is stored server-side (GEMINI_API_KEY in Django settings).
 *
 * @param {Object} ticket   - Ticket context object (id, title, status, priority, queue, days_unresponded).
 * @param {Array}  history  - Conversation history in Gemini format:
 *                            [{ role: "user"|"model", parts: [{ text: string }] }, ...]
 * @param {string} message  - The latest user message to send.
 * @returns {Promise<{ reply: string } | { error: string }>}
 */
export const askGeminiAboutTicket = async (ticket, history, message) => {
  try {
    const response = await apiClient.post("/api/tickets/gemini-chat/", {
      ticket,
      history,
      message,
    });
    return { reply: response.data.reply || "" };
  } catch (error) {
    console.error("Error calling Gemini ticket chat:", error);
    const errorMsg =
      error.response?.data?.error ||
      "Unable to reach the AI assistant. Please try again.";
    return { error: errorMsg };
  }
};

// ---------- Assets (ConnectWise RMM managed inventory) ----------
export const fetchAssetsOverview = async (clientId = null) => {
  if (!clientId) return null;

  try {
    const response = await apiClient.get("/api/assets/overview/", {
      params: { client_id: clientId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching assets overview:", error);
    return null;
  }
};

export const fetchAssetsList = async (clientId = null, options = {}) => {
  if (!clientId) return null;

  const {
    page = 1,
    pageSize = 50,
    search = "",
    assetType = "",
    osName = "",
    status = "",
    sortBy = "hostname",
    sortDir = "asc",
  } = options;

  try {
    const params = { client_id: clientId, page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
    if (search) params.search = search;
    if (assetType) params.asset_type = assetType;
    if (osName) params.os_name = osName;
    if (status) params.status = status;

    const response = await apiClient.get("/api/assets/list/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching assets list:", error);
    return null;
  }
};

export const fetchAssetDetail = async (endpointId) => {
  if (!endpointId) return null;

  try {
    const response = await apiClient.get(`/api/assets/${encodeURIComponent(endpointId)}/`);
    return response.data?.asset || null;
  } catch (error) {
    console.error("Error fetching asset detail:", error);
    return null;
  }
};

export const fetchAssetsSyncStatus = async () => {
  try {
    const response = await apiClient.get("/api/assets/sync-status/");
    return response.data?.sync || null;
  } catch (error) {
    console.error("Error fetching assets sync status:", error);
    return null;
  }
};

export const fetchAssetCompanyMappings = async () => {
  try {
    const response = await apiClient.get("/api/assets/company-mappings/");
    return response.data;
  } catch (error) {
    console.error("Error fetching ConnectWise company mappings:", error);
    return null;
  }
};

export const saveAssetCompanyMapping = async (cwCompanyId, clientId) => {
  try {
    const response = await apiClient.post("/api/assets/company-mappings/save/", {
      cw_company_id: cwCompanyId,
      client_id: clientId,
    });
    return response.data;
  } catch (error) {
    console.error("Error saving ConnectWise company mapping:", error);
    return { error: error.response?.data?.error || "Unable to save mapping" };
  }
};

// ---------- Conversetal Chatbot Proxy (cross-ticket ticket-intelligence chat) ----------
/**
 * Send a question to the Conversetal ticket-intelligence chatbot, proxied
 * server-side through Django (which forwards to the chatbot microservice
 * with the internal API key attached).
 *
 * @param {Array}  history  - Conversation history: [{ role: "user"|"assistant", content: string }, ...]
 * @param {string} message  - The latest user message to send.
 * @param {number|string|null} [clientId] - Currently selected client to scope the answer to.
 *                                           Ignored server-side for non-staff users (already
 *                                           locked to their own client); for staff/superadmin
 *                                           this narrows the chatbot's data to this client.
 * @returns {Promise<{ answer: string, sources: Array, fallback: boolean, citedTickets: Array } | { error: string }>}
 */
export const askChatbot = async (history, message, clientId = null) => {
  try {
    const response = await apiClient.post("/api/chatbot/chat/", {
      history,
      message,
      client_id: clientId,
    });
    return {
      answer: response.data.answer || "",
      sources: response.data.sources || [],
      fallback: !!response.data.fallback,
      citedTickets: response.data.cited_tickets || [],
    };
  } catch (error) {
    console.error("Error calling chatbot:", error);
    const errorMsg =
      error.response?.data?.error ||
      "Unable to reach the ticket assistant. Please try again.";
    return { error: errorMsg };
  }
};